import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AdminService } from 'src/app/services/admin-service';
import { EditorialIntelligenceService } from 'src/app/services/editorial-intelligence-service';
import {
  DossierSource,
  EditorialDecision,
  EditorialIntelligenceDossier,
  IntelClaim,
  IntelMedia,
  IntelSource,
  IntelTimelineEvent,
  MediaDiscoveryResult,
  NextAction,
  OutstandingTask,
  ReadinessCategory,
} from 'src/app/models/editorial/editorialIntelligence';

interface IntelTab {
  key: string;
  label: string;
}

@Component({
  selector: 'pla-admin-editorial-intelligence',
  templateUrl: './admin-editorial-intelligence.component.html',
  styleUrls: ['./admin-editorial-intelligence.component.scss'],
})
export class AdminEditorialIntelligenceComponent implements OnInit, OnDestroy {
  public verified = false; // admin check passed
  public loading = true;
  public notAvailable = false; // registration has no dossier (mock or api)

  public registration = '';
  public dossier: EditorialIntelligenceDossier | null = null;
  public dataSource: DossierSource | null = null;

  public activeTab = 'summary';
  // Tab keys are unchanged (routing/switch untouched). Only the 'gaps' LABEL is
  // updated to "Outstanding Tasks" per the V2 brief — the tab itself remains.
  public readonly tabs: IntelTab[] = [
    { key: 'summary', label: 'Intelligence Summary' },
    { key: 'readiness', label: 'Readiness' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'evidence', label: 'Evidence' },
    { key: 'sources', label: 'Sources' },
    { key: 'media', label: 'Media & Rights' },
    { key: 'comparables', label: 'Comparables' },
    { key: 'market', label: 'Market Analysis' },
    { key: 'gaps', label: 'Outstanding Tasks' },
    { key: 'story', label: 'Story Preview' },
    { key: 'brief', label: 'Story Brief (M2)' },
    { key: 'article', label: 'Article (M3)' },
    { key: 'studio', label: 'Creative Studio' },
  ];

  // Timeline expand/collapse state (by event index).
  public expandedEvents = new Set<number>();

  public toastMessage = '';
  private toastTimer: any = null;
  private routeSub?: Subscription;

  // ---- Workbench (M1 build / M2 brief / M3 article) ----
  public regInput = '';
  public building = false;
  public buildResult: any = null;
  public loadingBrief = false;
  public storyBrief: any = null;      // GET /story-brief response
  public generatingArticle = false;
  public article: any = null;         // POST /generate-article response

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService,
    private intel: EditorialIntelligenceService
  ) {}

  ngOnInit(): void {
    // Admin pages self-verify (no route guards in this app).
    this.adminService.verify().subscribe(
      (data: { admin: boolean }) => {
        if (!data || !data.admin) {
          this.router.navigate(['/landing']);
          return;
        }
        this.verified = true;
        this.watchRoute();
      },
      () => this.router.navigate(['/landing'])
    );
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  private watchRoute(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const param = params.get('registration');
      const reg = param ? this.intel.normalize(param) : this.intel.defaultRegistration;
      this.load(reg);
    });
  }

  private load(registration: string): void {
    this.registration = registration;
    this.regInput = registration;
    this.loading = true;
    this.notAvailable = false;
    this.dossier = null;
    this.dataSource = null;
    // Reset the M2/M3 workbench outputs when the subject changes.
    this.storyBrief = null;
    this.article = null;
    this.buildResult = null;

    this.intel.getDossier(registration).subscribe(
      (result) => {
        this.loading = false;
        if (!result) {
          this.notAvailable = true;
          return;
        }
        this.dossier = result.dossier;
        this.dataSource = result.source;
        this.activeTab = 'summary';
      },
      () => {
        this.loading = false;
        this.notAvailable = true;
      }
    );
  }

  public selectTab(key: string): void {
    this.activeTab = key;
  }

  public goToRegistration(reg: string): void {
    this.router.navigate(['/admin/editorial-intelligence', this.intel.normalize(reg)]);
  }

  // ---- Workbench actions ------------------------------------------------------

  /** Load whatever registration is typed in the workbench input. */
  public loadTyped(): void {
    const reg = this.intel.normalize(this.regInput);
    if (!reg) return;
    this.goToRegistration(reg);
  }

  /** 1 · Build the evidence dossier (M1), then reload the dossier in place. */
  public buildDossier(): void {
    if (this.building || !this.registration) return;
    this.building = true;
    this.buildResult = null;
    this.toast('Building evidence dossier…');
    this.intel.buildDossier(this.registration, false).subscribe(
      (res) => {
        this.building = false;
        this.buildResult = res;
        if (res && res.status === 'built') {
          this.toast('Dossier built — refreshing evidence.');
          this.storyBrief = null;
          this.article = null;
          this.load(this.registration); // re-fetch the freshly-built dossier
        } else {
          this.toast(res?.detail || 'Build failed.');
        }
      },
      () => {
        this.building = false;
        this.toast('Build failed.');
      }
    );
  }

  /** 3 · Fetch the Story Detection + Editorial Brief (M2) and open the tab. */
  public loadStoryBrief(): void {
    if (this.loadingBrief || !this.registration) return;
    this.loadingBrief = true;
    this.storyBrief = null;
    this.toast('Loading story brief…');
    this.intel.getStoryBrief(this.registration).subscribe(
      (res) => {
        this.loadingBrief = false;
        this.storyBrief = res;
        if (!res) {
          this.toast('No story brief — build the dossier first.');
          return;
        }
        this.selectTab('brief');
        this.toast(
          res.verdict === 'READY_FOR_BRIEF'
            ? 'Story brief ready.'
            : 'Not ready for article — see reasons.'
        );
      },
      () => {
        this.loadingBrief = false;
        this.toast('Story brief request failed.');
      }
    );
  }

  /** 4 · Generate + persist the deterministic article (M3) and open the tab. */
  public generateArticle(): void {
    if (this.generatingArticle || !this.registration) return;
    this.generatingArticle = true;
    this.article = null;
    this.toast('Generating article…');
    this.intel.generateArticle(this.registration).subscribe(
      (res) => {
        this.generatingArticle = false;
        this.article = res;
        this.selectTab('article');
        if (res?.status === 'generated') this.toast('Article generated.');
        else if (res?.status === 'not_ready') this.toast('Not ready for article — see reasons.');
        else this.toast(res?.detail || 'Article generation failed.');
      },
      () => {
        this.generatingArticle = false;
        this.toast('Article generation failed.');
      }
    );
  }

  public rankedTop(sb: any): any[] {
    const rs = sb?.storyDetection?.rankedStories || [];
    return rs.filter((s: any) => s.score > 0).slice(0, 6);
  }

  public get availableRegistrations(): string[] {
    return this.intel.availableRegistrations();
  }

  // ---- Plate Viewer deep-link -------------------------------------------------

  public get plateViewerLink(): any[] {
    return ['/plate-viewer', 'plate', this.registration];
  }

  // ---- Readiness helpers ------------------------------------------------------

  public band(value: number): 'ok' | 'warn' | 'bad' {
    if (value >= 70) return 'ok';
    if (value >= 40) return 'warn';
    return 'bad';
  }

  /** Ten segment cells; the first N reflect the value. */
  public segments(value: number): boolean[] {
    const filled = Math.round(value / 10);
    return Array.from({ length: 10 }, (_, i) => i < filled);
  }

  public overallReadiness(): number {
    const r = this.dossier?.readiness || [];
    if (!r.length) return 0;
    return Math.round(r.reduce((s, c: ReadinessCategory) => s + c.value, 0) / r.length);
  }

  // ---- Tier / usage / licence presentation -----------------------------------

  public tierLabel(tier?: number): string {
    return tier ? `T${tier}` : '—';
  }

  public tierClass(tier?: number): string {
    switch (tier) {
      case 1:
      case 2:
        return 'ok';
      case 3:
        return 'info';
      case 4:
        return 'warn';
      default:
        return 'bad';
    }
  }

  public usageLabel(policy?: string): string {
    switch (policy) {
      case 'publishable_fact':
        return 'Publishable fact';
      case 'publishable_context':
        return 'Publishable context';
      case 'internal_lead':
        return 'Internal lead';
      case 'triage_only':
        return 'Triage only';
      default:
        return policy || '—';
    }
  }

  public usageClass(policy?: string): string {
    switch (policy) {
      case 'publishable_fact':
        return 'ok';
      case 'publishable_context':
        return 'info';
      case 'internal_lead':
        return 'warn';
      default:
        return 'bad';
    }
  }

  public licenceClass(status?: string): string {
    switch (status) {
      case 'platex_owned':
      case 'public_domain':
      case 'owned':
      case 'licensed':
        return 'ok';
      case 'embed_only':
      case 'embed_allowed':
        return 'info';
      case 'permission_required':
        return 'warn';
      case 'licence_required':
      case 'do_not_use':
        return 'bad';
      default:
        return 'muted'; // reference_only / unknown
    }
  }

  // Human label for a rights category (the raw codes are not editor-friendly).
  public licenceLabel(status?: string): string {
    switch (status) {
      case 'platex_owned':
        return 'PlateX owned';
      case 'public_domain':
        return 'Public domain / CC';
      case 'embed_only':
        return 'Embed only';
      case 'licence_required':
        return 'Licence required';
      case 'permission_required':
        return 'Permission required';
      case 'unknown':
        return 'Unknown — review';
      // Legacy values.
      case 'embed_allowed':
        return 'Embed allowed';
      case 'owned':
        return 'Owned';
      case 'licensed':
        return 'Licensed';
      case 'do_not_use':
        return 'Do not use';
      case 'reference_only':
        return 'Reference only';
      default:
        return status || 'unknown';
    }
  }

  public confClass(label?: string): string {
    switch ((label || '').toLowerCase()) {
      case 'high':
        return 'ok';
      case 'medium':
        return 'info';
      case 'low':
        return 'warn';
      default:
        return 'bad';
    }
  }

  public severityClass(severity?: string): string {
    switch (severity) {
      case 'high':
        return 'bad';
      case 'medium':
        return 'warn';
      default:
        return 'info';
    }
  }

  public provenanceClass(p?: string): string {
    switch (p) {
      case 'authentic':
        return 'authentic';
      case 'render':
        return 'owned';
      case 'concept':
        return 'concept';
      default:
        return 'muted';
    }
  }

  public provenanceLabel(p?: string): string {
    switch (p) {
      case 'authentic':
        return 'Authentic media';
      case 'render':
        return 'PlateX render';
      case 'concept':
        return 'AI concept';
      default:
        return 'Empty slot';
    }
  }

  public statusClass(status?: string): string {
    switch (status) {
      case 'approved':
        return 'ok';
      case 'rejected':
        return 'bad';
      case 'in_review':
      case 'changes_requested':
      case 're_research':
        return 'warn';
      default:
        return 'muted';
    }
  }

  // ---- Evidence helpers -------------------------------------------------------

  public sourceById(id: number): IntelSource | undefined {
    return this.dossier?.sources.find((s) => s.id === id);
  }

  public displayableMediaCount(): number {
    return (this.dossier?.media || []).filter(
      (m) => m.approvedForDisplay && m.displayAllowed
    ).length;
  }

  // ---- Media grouping (Media & Rights tab) ------------------------------------
  // The Media Centre presents FIVE distinct buckets so nothing weak is ever shown
  // as a success, and nothing a human could find is silently dropped:
  //   1. Discovered media (publish-ready)  — live hits whose rights we can clear
  //      (PlateX-owned / public-domain / embed-only); still editor-gated.
  //   2. Reference / rights required       — live hits recorded but NOT publishable
  //      (licence-required / permission-required / reference-only / unknown).
  //   3. Generated requirements            — chart/map placeholders.
  //   4. Fixture / reference-only media     — seeded/uploaded/dealer/archive rows.
  //   5. Retired & rejected                — system-retired or editor-rejected.

  // Rights categories we could actually publish (still behind approved_for_display).
  private static readonly PUBLISHABLE_RIGHTS = new Set<string>([
    'platex_owned',
    'public_domain',
    'embed_only',
    // Legacy equivalents.
    'owned',
    'licensed',
    'embed_allowed',
  ]);

  private get allMedia(): IntelMedia[] {
    return this.dossier?.media || [];
  }

  /** True when the rights category is one we could clear for display. */
  private isPublishableRights(m: IntelMedia): boolean {
    return AdminEditorialIntelligenceComponent.PUBLISHABLE_RIGHTS.has(
      m.licenceStatus || 'unknown'
    );
  }

  /** True once an item is out of the active set (system-retired or editor-rejected). */
  private isInactive(m: IntelMedia): boolean {
    return m.status === 'retired' || m.status === 'rejected';
  }

  /**
   * A row is LIVE-discovered only if a provider produced it in a discovery run —
   * i.e. source_type 'discovered' AND it carries a discoveryRunId. Fixture /
   * reference rows are seeded outside discovery (no run id), even when their
   * source_type defaults to 'discovered', so they must NOT be shown as real
   * discovered media.
   */
  private isLiveDiscovered(m: IntelMedia): boolean {
    return (m.sourceType || 'discovered') === 'discovered' && !!m.discoveryRunId;
  }

  /** Real third-party assets a provider found and kept (active only). */
  public realDiscoveredMedia(): IntelMedia[] {
    return this.allMedia.filter(
      (m) => this.isLiveDiscovered(m) && !this.isInactive(m)
    );
  }

  /**
   * Discovered media we could actually PUBLISH (rights are clearable: PlateX-owned,
   * public-domain or embed-only). Still editor-gated by approvedForDisplay.
   */
  public publishReadyMedia(): IntelMedia[] {
    return this.realDiscoveredMedia().filter((m) => this.isPublishableRights(m));
  }

  /**
   * Discovered assets recorded for REFERENCE only — rights are not clearable
   * without action (licence required, permission required, reference-only, or
   * unknown). Recorded and rights-flagged, but NEVER presented as publish-ready.
   */
  public referenceRequiredMedia(): IntelMedia[] {
    return this.realDiscoveredMedia().filter((m) => !this.isPublishableRights(m));
  }

  /** Chart/map placeholders — generated requirements, NOT discovered media. */
  public generatedRequirements(): IntelMedia[] {
    return this.allMedia.filter(
      (m) => m.sourceType === 'generated' && !this.isInactive(m)
    );
  }

  /**
   * Fixture / reference-only media: anything active that is neither a live
   * discovery hit nor a generated requirement — uploads, dealer-supplied,
   * historic archive, and seeded example rows (source_type 'discovered' with no
   * discoveryRunId).
   */
  public fixtureReferenceMedia(): IntelMedia[] {
    return this.allMedia.filter(
      (m) =>
        !this.isInactive(m) &&
        m.sourceType !== 'generated' &&
        !this.isLiveDiscovered(m)
    );
  }

  /** Retired (system) or rejected (editor) items — kept for audit, hidden from active. */
  public retiredRejectedMedia(): IntelMedia[] {
    return this.allMedia.filter((m) => this.isInactive(m));
  }

  /** Publish-ready discovered media (clearable rights; excludes reference assets). */
  public discoveredMediaCount(): number {
    return this.publishReadyMedia().length;
  }

  /** Reference/rights-required discovered assets (recorded, not publish-ready). */
  public referenceRequiredCount(): number {
    return this.referenceRequiredMedia().length;
  }

  /** Chart/map placeholders — generated requirements, NOT discovered media. */
  public generatedMediaCount(): number {
    return this.generatedRequirements().length;
  }

  /** Count of retired/rejected items (shown in the header for transparency). */
  public retiredRejectedCount(): number {
    return this.retiredRejectedMedia().length;
  }

  // ---- Media Discovery (v3) ---------------------------------------------------

  /** True while a discovery run is in flight (disables the button). */
  public discovering = false;
  /** Summary of the last discovery run (honest counts + not-configured note). */
  public discoveryResult: MediaDiscoveryResult | null = null;
  /** Media ids with an action request in flight. */
  public mediaActionPending = new Set<string>();
  /** Card whose inline note editor is open, and the draft text. */
  public noteEditorId: string | null = null;
  public noteDraft = '';

  private mediaKey(m: IntelMedia): string {
    return String(m.id ?? '');
  }

  /** Run server-side Media Discovery, then refresh the dossier on success. */
  public discoverMedia(): void {
    if (this.discovering || !this.registration) return;
    this.discovering = true;
    this.toast('Discovering media…');
    this.intel.discoverMedia(this.registration).subscribe(
      (res) => {
        this.discovering = false;
        this.discoveryResult = res;
        if (res.status === 'completed') {
          // Lead with the honest summary the backend computed (distinguishes real
          // discovered media from generated chart/map requirements, and flags any
          // search provider that is not configured).
          this.toast(res.message || `Discovery complete — ${res.mediaDiscovered ?? 0} media found.`);
          this.refreshDossier();
        } else if (res.status === 'egress_unavailable') {
          this.toast(res.detail || 'Media discovery could not reach the internet from this environment.');
        } else {
          this.toast(res.detail || 'No dossier to discover media for.');
        }
      },
      () => {
        this.discovering = false;
        this.toast('Media discovery failed.');
      }
    );
  }

  /** Re-fetch the dossier in place (keeps the user on the Media tab). */
  private refreshDossier(): void {
    this.intel.getDossier(this.registration).subscribe((result) => {
      if (result) {
        this.dossier = result.dossier;
        this.dataSource = result.source;
      }
    });
  }

  /** After a manual reference import, refresh the dossier so new media appears. */
  public onReferencesImported(): void {
    this.refreshDossier();
    this.toast('Imported references added — media refreshed.');
  }

  // ---- Thumbnails (remote references only; never downloaded/rehosted) ----------

  /** Remote thumbnails that failed to load — fall back to the placeholder. */
  public brokenThumbs = new Set<string>();

  /** The remote thumbnail to show: the stored one, else a derived YouTube frame. */
  public thumbFor(m: IntelMedia): string | null {
    if (m.thumbnailUrl) return m.thumbnailUrl;
    const vid = this.youtubeId(m.url || m.embedUrl);
    return vid ? `https://i.ytimg.com/vi/${vid}/hqdefault.jpg` : null;
  }

  private youtubeId(u?: string): string | null {
    if (!u) return null;
    const m = /(?:v=|youtu\.be\/|\/embed\/)([A-Za-z0-9_-]{11})/.exec(u);
    return m ? m[1] : null;
  }

  public onThumbError(m: IntelMedia): void {
    this.brokenThumbs.add(String(m.id ?? m.url));
  }

  public thumbBroken(m: IntelMedia): boolean {
    return this.brokenThumbs.has(String(m.id ?? m.url));
  }

  public mediaStatusClass(status?: string): string {
    switch (status) {
      case 'approved':
        return 'ok';
      case 'rejected':
        return 'bad';
      case 'flagged':
        return 'warn';
      case 'retired':
        return 'muted';
      default:
        return 'muted'; // pending
    }
  }

  /** Editor-friendly label for a media review status. */
  public mediaStatusLabel(status?: string): string {
    switch (status) {
      case 'retired':
        return 'retired (stale)';
      default:
        return status || 'pending';
    }
  }

  /** Relevance as an integer percentage for display, or null if unscored. */
  public relevancePct(m: IntelMedia): number | null {
    if (m.relevanceScore == null) return null;
    return Math.round(m.relevanceScore * 100);
  }

  public isMediaPending(m: IntelMedia): boolean {
    return this.mediaActionPending.has(this.mediaKey(m));
  }

  private applyMediaAction(
    m: IntelMedia,
    action: 'approve' | 'reject' | 'flag' | 'note',
    notes?: string
  ): void {
    const id = m.id;
    if (id == null) {
      this.toast('This media item has no id yet — run discovery first.');
      return;
    }
    const key = this.mediaKey(m);
    this.mediaActionPending.add(key);
    this.intel.mediaAction(id, action, notes).subscribe(
      (ok) => {
        this.mediaActionPending.delete(key);
        if (!ok) {
          this.toast('Action failed — no changes were made.');
          return;
        }
        // Optimistic local update so the card reflects the decision immediately.
        if (action === 'approve') {
          m.status = 'approved';
          m.approvedForDisplay = true;
        } else if (action === 'reject') {
          m.status = 'rejected';
          m.approvedForDisplay = false;
        } else if (action === 'flag') {
          m.status = 'flagged';
        } else if (action === 'note') {
          m.editorNotes = notes ?? m.editorNotes;
        }
        this.toast('Saved.');
      },
      () => {
        this.mediaActionPending.delete(key);
        this.toast('Action failed — no changes were made.');
      }
    );
  }

  public approveMedia(m: IntelMedia): void {
    this.applyMediaAction(m, 'approve');
  }

  public rejectMedia(m: IntelMedia): void {
    this.applyMediaAction(m, 'reject');
  }

  public flagMedia(m: IntelMedia): void {
    this.applyMediaAction(m, 'flag');
  }

  public startNote(m: IntelMedia): void {
    this.noteEditorId = this.mediaKey(m);
    this.noteDraft = m.editorNotes || '';
  }

  public cancelNote(): void {
    this.noteEditorId = null;
    this.noteDraft = '';
  }

  public isNoteOpen(m: IntelMedia): boolean {
    return this.noteEditorId === this.mediaKey(m);
  }

  public saveNote(m: IntelMedia): void {
    const note = (this.noteDraft || '').trim();
    this.applyMediaAction(m, 'note', note);
    this.cancelNote();
  }

  public piiSourceCount(): number {
    return (this.dossier?.sources || []).filter((s) => s.piiPresent).length;
  }

  // ---- V2: Editorial Decision -------------------------------------------------

  public recommendationLabel(rec?: string): string {
    switch (rec) {
      case 'ready':
        return 'Ready to publish';
      case 'ready_conditions':
        return 'Ready with conditions';
      case 'research_required':
        return 'Research required';
      case 'not_suitable':
        return 'Not suitable';
      default:
        return '—';
    }
  }

  public recommendationClass(rec?: string): string {
    switch (rec) {
      case 'ready':
        return 'ok';
      case 'ready_conditions':
        return 'warn';
      case 'research_required':
        return 'info';
      case 'not_suitable':
        return 'bad';
      default:
        return 'muted';
    }
  }

  /** Overall story score: explicit override, else the mean of the metrics. */
  public storyScore(decision?: EditorialDecision): number {
    if (!decision) return 0;
    if (decision.storyScore.overrideOverall != null) {
      return Math.round(decision.storyScore.overrideOverall);
    }
    const m = decision.storyScore.metrics || [];
    if (!m.length) return 0;
    return Math.round(m.reduce((s, x) => s + x.value, 0) / m.length);
  }

  public scoreBand(value: number): 'ok' | 'warn' | 'bad' {
    return this.band(value);
  }

  // ---- V2: readiness facets ---------------------------------------------------

  public stateClass(state?: string): string {
    switch (state) {
      case 'complete':
        return 'ok';
      case 'partial':
        return 'warn';
      default:
        return 'bad'; // outstanding
    }
  }

  public stateLabel(state?: string): string {
    switch (state) {
      case 'complete':
        return 'Complete';
      case 'partial':
        return 'Partial';
      case 'outstanding':
        return 'Outstanding';
      default:
        return '—';
    }
  }

  // ---- V2: next actions -------------------------------------------------------

  public runAction(action: NextAction): void {
    if (action.enabled && action.link) {
      this.router.navigate(action.link);
      return;
    }
    this.toast(action.label + ' — prototype action (not wired yet).');
  }

  // ---- V2: timeline expand / linked evidence ---------------------------------

  public toggleEvent(index: number): void {
    if (this.expandedEvents.has(index)) this.expandedEvents.delete(index);
    else this.expandedEvents.add(index);
  }

  public isExpanded(index: number): boolean {
    return this.expandedEvents.has(index);
  }

  /** Claims whose sources overlap this event's sources = linked evidence. */
  public linkedEvidence(event: IntelTimelineEvent): IntelClaim[] {
    const ids = event.sourceIds || [];
    if (!ids.length || !this.dossier) return [];
    return this.dossier.claims.filter((c) =>
      (c.sourceIds || []).some((s) => ids.includes(s))
    );
  }

  // ---- V2: evidence purpose badges -------------------------------------------

  public purposeLabel(p: string): string {
    return 'Supports ' + p;
  }

  // ---- V2: source enrichment (placeholders where data is unavailable) --------

  public discoveryFor(s: IntelSource): string {
    if (s.discoveryMethod) return s.discoveryMethod;
    switch (s.accessMethod) {
      case 'manual':
        return 'Internal export';
      case 'direct_fetch':
        return 'Direct fetch';
      case 'search_index':
        return 'Web search';
      case 'api':
        return 'API';
      default:
        return 'Not recorded';
    }
  }

  public lastVerifiedFor(s: IntelSource): string {
    return s.lastVerified || s.dateAccessed || 'Not verified';
  }

  public licenceFor(s: IntelSource): string {
    if (s.licence) return s.licence;
    if (s.tier === 1) return 'Owned (internal)';
    if (s.tier === 2) return 'Editorial reference';
    if (s.tier === 3) return 'Cite & link';
    return 'Reference only';
  }

  public archivedLabel(s: IntelSource): string {
    return s.archived ? 'Archived' : 'Not archived';
  }

  // ---- V2: outstanding tasks --------------------------------------------------

  public priorityClass(priority?: string): string {
    switch (priority) {
      case 'high':
        return 'bad';
      case 'medium':
        return 'warn';
      default:
        return 'info';
    }
  }

  public openTaskCount(): number {
    return (this.dossier?.outstandingTasks || []).filter((t) => !t.completed).length;
  }

  /** Local-only prototype toggle (no backend write). */
  public toggleTask(task: OutstandingTask): void {
    task.completed = !task.completed;
    this.toast(
      task.completed ? 'Marked done (prototype).' : 'Reopened (prototype).'
    );
  }

  // ---- Prototype-only affordances (no generation / no writes) -----------------

  public toast(message: string): void {
    this.toastMessage = message;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toastMessage = ''), 3200);
  }
}
